/**
 * ArchLang v0.10 — lens resolution (VIEWS.md §8, S18 engine layer).
 *
 * `computeLensEntries(model, view, subjectKey, ctx)` merges the three lens
 * layers for one inspected element and evaluates each entry with `this` bound
 * to it — the data the inspector (DetailsPane/Sidebar) renders:
 *
 *   1. **Top-level** `lens { … }` declarations ({@link ResolvedModel.lenses})
 *      apply inspector-wide in their declaring space's scope (a root-space
 *      lens applies model-wide).
 *   2. **Module-body** lens blocks (`Body.lenses`) apply to the owning
 *      module's SUBTREE.
 *   3. The **active view's** `lens` clauses add or override **by entry name**
 *      (nearer layer wins; an override keeps the original entry's position,
 *      additions append) — pass `view = null` when no view is active.
 *
 * Pure over the landed expression evaluator (`evalExpression`) — no scene
 * types, no rendering. Deferred builtins / list-valued getters read as `null`
 * (the same absent-cell posture as `computeTableData`).
 */

import type { QualifiedName } from "./types.js";
import type { LensEntry, ViewDeclV2 } from "./v010-ast.js";
import type { ResolvedModel, ResolvedModule } from "./resolver.js";
import type { ElementNode, EvalContext } from "./selector-eval.js";
import { evalExpression, UnsupportedExprError } from "./expression-eval.js";

/** One resolved inspector line: entry identity + display title + scalar value. */
export interface LensEntryData {
  readonly name: string;
  /** The entry's string title, else its name verbatim. */
  readonly title: string;
  /** JSON-clean scalar; `null` = absent / deferred. */
  readonly value: string | number | boolean | null;
}

/**
 * Resolve + evaluate the lens entries visible on `subjectKey`. Unknown subject
 * → `[]` (nothing inspectable). `view` = the ACTIVE view or `null`.
 */
export function computeLensEntries(
  model: ResolvedModel,
  view: ViewDeclV2 | null,
  subjectKey: string,
  ctx: EvalContext,
): LensEntryData[] {
  const subject = ctx.universe.byKey(subjectKey);
  if (!subject) return [];

  // Ordered name→entry merge: later layers override IN PLACE by name.
  const merged = new Map<string, LensEntry>();
  const put = (entries: readonly LensEntry[]): void => {
    for (const e of entries) merged.set(e.name, e);
  };

  // 1. Top-level lenses, space-scoped (root-space lens = model-wide).
  for (const decl of model.lenses ?? []) {
    const lensSpace = decl.source?.file ? model.spaces?.get(decl.source.file) : undefined;
    if (inSpaceScope(lensSpace, subject)) put(decl.entries);
  }

  // 2. Module-body lenses along the subject's ancestor chain (owner subtree).
  for (const owner of ownersOf(model.modules, subject.path)) {
    for (const decl of owner.body.lenses ?? []) put(decl.entries);
  }

  // 3. The active view's lens clauses (add/override by entry name).
  if (view) {
    for (const clause of view.clauses) {
      if (clause.kind === "lens") put(clause.entries);
    }
  }

  return [...merged.values()].map((e) => ({
    name: e.name,
    title: e.title ?? e.name,
    value: evalEntry(e, subject, ctx),
  }));
}

/** A lens declared in space `lensSpace` sees `subject` iff the subject's space
 *  sits at-or-below it. No lens space (root) = the whole model. */
function inSpaceScope(lensSpace: QualifiedName | undefined, subject: ElementNode): boolean {
  if (!lensSpace || lensSpace.length === 0) return true;
  const s = subject.space;
  if (!s || s.length < lensSpace.length) return false;
  return lensSpace.every((seg, i) => s[i] === seg);
}

/** The resolved modules whose subtree contains `path` (every proper ancestor
 *  AND the element's own module when the path names one), outermost first. */
function ownersOf(modules: readonly ResolvedModule[], path: QualifiedName): ResolvedModule[] {
  const out: ResolvedModule[] = [];
  let level = modules;
  for (const seg of path) {
    const m = level.find((x) => x.name === seg);
    if (!m) break;
    out.push(m);
    level = m.modules;
  }
  return out;
}

/** Evaluate one entry with `this` = subject; deferred/absent reads as `null`. */
function evalEntry(e: LensEntry, subject: ElementNode, ctx: EvalContext): LensEntryData["value"] {
  try {
    const v = evalExpression(e.expr, subject, { ...ctx, subject });
    return v === undefined ? null : v;
  } catch (err) {
    if (err instanceof UnsupportedExprError) return null;
    throw err;
  }
}
